import { createAsyncTypes } from 'store/utils';
import { Configuration } from 'store/configurations';
import { MediaTypes, MediaResponse, MediaType } from 'views/MediaCenterView/types';

// State Key
export const KEY = 'media';

// Action Types
export const types = {
  CREATE_MEDIA: createAsyncTypes(`${KEY}/CREATE_MEDIA`),
  UPDATE_MEDIA: createAsyncTypes(`${KEY}/UPDATE_MEDIA`),
  GET_MEDIA: createAsyncTypes(`${KEY}/GET_MEDIA`),
  DELETE_MEDIA: createAsyncTypes(`${KEY}/DELETE_MEDIA`),
  DELETE_ASSOCIATED_MEDIA: createAsyncTypes(`${KEY}/DELETE_ASSOCIATED_MEDIA`),
  DOWNLOAD_MEDIA: createAsyncTypes(`${KEY}/DOWNLOAD_MEDIA`),
  RESET_ACTION: `${KEY}/RESET_ACTION`,
  RESET_MEDIA: `${KEY}/RESET_MEDIA`,
  SET_CONFIGURATION: `${KEY}/SET_CONFIGURATION`,
  SET_ATTRACT_MEDIA_TYPE: `${KEY}/SET_ATTRACT_MEDIA_TYPE`,
  SET_ENVIRONMENT: `${KEY}/SET_ENVIRONMENT`,
  SET_EVENT: `${KEY}/SET_EVENT`,
  SET_VENUE: `${KEY}/SET_VENUE`,
  SET_GENRE: `${KEY}/SET_GENRE`
};

export interface Media {
  id: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  file: any;
  overwrite?: boolean;
}

export interface ClientIntegration {
  id: string;
  clientId: string;
  baseUrl: string;
  name: string;
  integration: {
    id: string;
    name: string;
    code: string;
  };
}

export interface MediaState {
  readonly action: {
    readonly successful: boolean;
    readonly pending: boolean;
    readonly error: string | null;
    readonly response: MediaResponse | null;
  };
  readonly configuration: Configuration | null;
  readonly environment: ClientIntegration | null;
  readonly attract: {
    readonly mediaType: MediaType | null;
    readonly media?: MediaResponse[];
  };
  readonly otherImages: {
    readonly inactive?: MediaResponse[];
    readonly splash?: MediaResponse[];
    readonly logo?: MediaResponse[];
    readonly logoLarge?: MediaResponse[];
    readonly background?: MediaResponse[];
    readonly promoCode?: MediaResponse[];
    readonly kioskGraphic?: MediaResponse[];
  };
  readonly event: {
    readonly selected: string;
    readonly media?: MediaResponse[];
  };
  readonly venue: {
    readonly selected: string;
    readonly media?: MediaResponse[];
  };
  readonly genre: {
    readonly selected: string;
    readonly media?: MediaResponse[];
  };
}

export interface ResetAction {
  type: typeof types.RESET_ACTION;
}

export interface CreateMediaParams {
  id: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  file: any;
}

export interface UpdateMediaParams {
  oldName: string;
  newName: string;
}

export interface CreateMediaRequest {
  type: typeof types.CREATE_MEDIA.REQUEST;
  payload: Media;
}

export interface UploadImageError {
  type: typeof types.CREATE_MEDIA.ERROR;
  payload: string;
}

export interface UploadImagePending {
  type: typeof types.CREATE_MEDIA.PENDING;
  payload: boolean;
}

export interface UploadImageSuccess {
  type: typeof types.CREATE_MEDIA.SUCCESS;
  payload: MediaResponse;
}

export interface UpdateMediaRequest {
  type: typeof types.UPDATE_MEDIA.REQUEST;
  payload: UpdateMediaParams;
}

export interface RenameImageError {
  type: typeof types.UPDATE_MEDIA.ERROR;
  payload: string;
}

export interface RenameImagePending {
  type: typeof types.UPDATE_MEDIA.PENDING;
  payload: boolean;
}

export interface RenameImageSuccess {
  type: typeof types.UPDATE_MEDIA.SUCCESS;
  payload: string;
}

export interface ConfigurationParams {
  type: typeof types.SET_CONFIGURATION;
  payload: Configuration;
}

export interface AttractMediaType {
  type: typeof types.SET_ATTRACT_MEDIA_TYPE;
  payload: MediaType;
}

export interface Environment {
  type: typeof types.SET_ENVIRONMENT;
  payload: ClientIntegration;
}

export interface SetEvent {
  type: typeof types.SET_EVENT;
  payload: string;
}

export interface SetVenue {
  type: typeof types.SET_VENUE;
  payload: string;
}

export interface SetGenre {
  type: typeof types.SET_GENRE;
  payload: string;
}

export interface GetMediaParams {
  type: string;
  clientIntegrationId?: string;
  configurationId?: string;
  name?: string;
}

export interface GetMediaResponse {
  type: string;
  event?: MediaResponse[];
  venue?: MediaResponse[];
  genre?: MediaResponse[];
  [MediaTypes.ATTRACT_IMAGE]?: MediaResponse[];
  [MediaTypes.ATTRACT_VIDEO]?: MediaResponse[];
  [MediaTypes.INACTIVE_IMAGE]?: MediaResponse[];
  [MediaTypes.SPLASH_IMAGE]?: MediaResponse[];
  [MediaTypes.PROMO_CODE_IMAGE]?: MediaResponse[];
  [MediaTypes.KIOSK_GRAPHIC]?: MediaResponse[];
  [MediaTypes.LOGO_IMAGE]?: MediaResponse[];
  [MediaTypes.LOGO_IMAGE_LARGE]?: MediaResponse[];
  [MediaTypes.BACKGROUND_IMAGE]?: MediaResponse[];
}

export interface GetMediaRequest {
  type: typeof types.GET_MEDIA.REQUEST;
  payload: GetMediaParams;
}

export interface GetMediaError {
  type: typeof types.GET_MEDIA.ERROR;
  payload: string;
}

export interface GetMediaPending {
  type: typeof types.GET_MEDIA.PENDING;
  payload: boolean;
}

export interface GetMediaSuccess {
  type: typeof types.GET_MEDIA.SUCCESS;
  payload: GetMediaResponse;
}

export interface DeleteMediaRequest {
  type: typeof types.DELETE_MEDIA.REQUEST;
  payload: string;
}

export interface DeleteAssociatedMediaRequest {
  type: typeof types.DELETE_ASSOCIATED_MEDIA.REQUEST;
  payload: {
    clientIntegrationId?: string;
    configurationId?: string;
  };
}

export interface DownloadMediaRequest {
  type: typeof types.DOWNLOAD_MEDIA.REQUEST;
  payload: string;
}

export interface DownloadMediaPending {
  type: typeof types.DOWNLOAD_MEDIA.PENDING;
  payload: boolean;
}

export interface DownloadMediaError {
  type: typeof types.DOWNLOAD_MEDIA.ERROR;
  payload: string;
}

export interface DownloadMediaSuccess {
  type: typeof types.DOWNLOAD_MEDIA.SUCCESS;
}

export type MediaActionTypes =
  | CreateMediaRequest
  | UploadImagePending
  | UploadImageSuccess
  | UploadImageError
  | UpdateMediaRequest
  | RenameImagePending
  | RenameImageSuccess
  | RenameImageError
  | ResetAction
  | ConfigurationParams
  | AttractMediaType
  | Environment
  | SetEvent
  | SetVenue
  | SetGenre
  | GetMediaParams
  | GetMediaRequest
  | GetMediaError
  | GetMediaPending
  | GetMediaSuccess
  | DeleteMediaRequest
  | DeleteAssociatedMediaRequest
  | DownloadMediaRequest
  | DownloadMediaPending
  | DownloadMediaSuccess
  | DownloadMediaError;
